import { useState, useEffect } from 'react';
import { View, Text, ScrollView, Pressable, Alert, Modal, Platform, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuthStore } from '../../../store/authStore';
import { authApi } from '../../../shared/utils/api';
import { useStyles } from '../../../hooks/useStyles';
import { useResponsive, getResponsiveValue } from '../../../shared/utils/responsive';
import { addColorOpacity } from '../../../shared/utils/styles';
import { Portfolio } from '../../../shared/types/portfolio';
import { Input } from '../../../components/ui';
import { BaseCard } from '../../../components/ui';
import { Ionicons } from '@expo/vector-icons';
import { PageContainer, PageHeader } from '../../../components/layout';
import { useAnalytics } from '../../../hooks/useAnalytics';

export default function PortfoliosPage() {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [portfolioName, setPortfolioName] = useState('');
  const [portfolioDescription, setPortfolioDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const router = useRouter();
  const styles = useStyles();
  const { tokens, utils } = styles;
  const responsive = useResponsive();
  const { isMobile, isSmallMobile } = responsive;
  const { trackPageView, trackFeature } = useAnalytics();

  // Track page view
  useEffect(() => {
    trackPageView('/portfolios');
  }, [trackPageView]);

  useEffect(() => {
    loadPortfolios();
  }, []);

  const loadPortfolios = async () => {
    try {
      setLoading(true);
      const response = await authApi.getPortfolios();
      setPortfolios(response.portfolios);
    } catch (error: any) {
      // Sanitize error messages - show generic message to prevent information leakage
      const safeMessage = 'Failed to load portfolios. Please try again.';
      Alert.alert('Error', safeMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePortfolio = async () => {
    // Validate and sanitize input
    const trimmedName = portfolioName.trim();
    const trimmedDescription = portfolioDescription.trim();
    
    if (!trimmedName) {
      Alert.alert('Error', 'Portfolio name is required');
      return;
    }

    // Client-side length validation (defense in depth)
    if (trimmedName.length > 255) {
      Alert.alert('Error', 'Portfolio name must be 255 characters or less');
      return;
    }

    if (trimmedDescription.length > 1000) {
      Alert.alert('Error', 'Portfolio description must be 1000 characters or less');
      return;
    }

    setCreating(true);
    try {
      await authApi.createPortfolio({
        name: trimmedName,
        description: trimmedDescription || null,
      });
      setCreateModalVisible(false);
      setPortfolioName('');
      setPortfolioDescription('');
      await loadPortfolios();
      Alert.alert('Success', 'Portfolio created successfully');
    } catch (error: any) {
      // Sanitize error messages - show generic message to prevent information leakage
      const errorMessage = error?.message || 'Failed to create portfolio';
      // Only show specific validation errors, otherwise generic message
      const safeMessage = errorMessage.includes('Validation error') || errorMessage.includes('required') || errorMessage.includes('length')
        ? errorMessage
        : 'Failed to create portfolio. Please try again.';
      Alert.alert('Error', safeMessage);
    } finally {
      setCreating(false);
    }
  };

  const handlePortfolioPress = (portfolioId: number) => {
    // Validate portfolioId is a valid number to prevent path traversal
    if (!portfolioId || typeof portfolioId !== 'number' || isNaN(portfolioId) || portfolioId <= 0) {
      Alert.alert('Error', 'Invalid portfolio');
      return;
    }
    router.push(`/(tabs)/portfolios/${portfolioId}`);
  };

  // Responsive values for typography - smaller on mobile for better fit
  const bodySize = isSmallMobile ? 13 : getResponsiveValue(14, 15, 15, 16);
  const buttonSize = isSmallMobile ? 14 : getResponsiveValue(15, 16, 17, 18);
  const cardTitleSize = isSmallMobile ? 15 : getResponsiveValue(16, 17, 18, 20);
  const cardBodySize = isSmallMobile ? 12 : getResponsiveValue(13, 14, 14, 15);
  const cardMetaSize = isSmallMobile ? 10 : getResponsiveValue(11, 12, 12, 13);

  // Grid columns based on breakpoint
  const gridColumns = responsive.isMobile ? 1 : responsive.isTablet ? 2 : 3;
  const cardGap = utils.spacing.lg;

  return (
    <PageContainer 
      verticalPadding="2xl"
      scrollViewProps={{ keyboardShouldPersistTaps: 'handled' }}
    >
      <PageHeader
        title="My Portfolios"
        action={
          <Pressable
            onPress={() => setCreateModalVisible(true)}
            style={({ pressed }) => [
              {
                backgroundColor: tokens.color.primary,
                minHeight: isSmallMobile ? 38 : getResponsiveValue(40, 44, 46, 48),
                paddingHorizontal: isSmallMobile ? utils.spacing.md : utils.spacing.xl,
                paddingVertical: isSmallMobile ? utils.spacing.xs : utils.spacing.md,
                borderRadius: utils.radius.md,
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                gap: isSmallMobile ? 4 : utils.spacing.xs,
                minWidth: isMobile ? undefined : 180,
                ...Platform.select({
                  web: {
                    boxShadow: `0 6px 20px ${addColorOpacity(tokens.color.primary, 0.35)}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    ':hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 28px ${addColorOpacity(tokens.color.primary, 0.45)}`,
                    },
                    ':active': {
                      transform: 'translateY(0px) scale(0.98)',
                    },
                  } as any,
                  default: {
                    shadowColor: tokens.color.primary,
                    shadowOffset: { width: 0, height: 6 },
                    shadowOpacity: 0.35,
                    shadowRadius: 20,
                    elevation: 8,
                    opacity: pressed ? 0.9 : 1,
                  },
                }),
              },
            ]}
          >
            <Ionicons 
              name="add" 
              size={isSmallMobile ? 16 : getResponsiveValue(18, 20, 21, 22)} 
              color={tokens.color.onPrimary} 
            />
            <Text style={{
              fontSize: buttonSize,
              fontWeight: utils.typography.weights.semibold,
              letterSpacing: 0.2,
              color: tokens.color.onPrimary,
              fontFamily: utils.typography.body.fontFamily,
            }}>
              {isSmallMobile ? 'Create' : 'Create Portfolio'}
            </Text>
          </Pressable>
        }
      />

      {/* Content */}
      {loading ? (
            <View 
              style={[
                styles.centered,
                {
                  paddingVertical: utils.spacing['3xl'],
                }
              ]}
            >
              <ActivityIndicator size="large" color={tokens.color.primary} />
              <Text style={[
                {
                  fontSize: bodySize,
                  color: addColorOpacity(tokens.color.onBackground, 0.6),
                  marginTop: utils.spacing.md,
                  fontFamily: utils.typography.body.fontFamily,
                }
              ]}>
                Loading portfolios...
              </Text>
            </View>
          ) : portfolios.length === 0 ? (
            <View>
              <BaseCard 
                padding={isSmallMobile ? 'lg' : '2xl'}
                style={{ 
                  alignItems: 'center',
                }}
              >
                <View style={{
                  width: isSmallMobile ? 60 : getResponsiveValue(70, 80, 90, 100),
                  height: isSmallMobile ? 60 : getResponsiveValue(70, 80, 90, 100),
                  borderRadius: utils.radius.xl,
                  backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: isSmallMobile ? utils.spacing.md : utils.spacing.lg,
                }}>
                  <Ionicons 
                    name="folder-outline" 
                    size={isSmallMobile ? 30 : getResponsiveValue(36, 40, 44, 48)} 
                    color={tokens.color.primary} 
                  />
                </View>
                <Text style={[
                  {
                    fontSize: isSmallMobile ? 16 : getResponsiveValue(18, 20, 21, 22),
                    fontWeight: utils.typography.weights.bold,
                    marginBottom: utils.spacing.sm,
                    color: tokens.color.onSurface,
                    fontFamily: utils.typography.heading.fontFamily,
                    textAlign: 'center',
                  }
                ]}>
                  No portfolios yet
                </Text>
                <Text style={[
                  {
                    fontSize: bodySize,
                    color: addColorOpacity(tokens.color.onSurface, 0.6),
                    textAlign: 'center',
                    fontFamily: utils.typography.body.fontFamily,
                    lineHeight: bodySize * 1.5,
                    maxWidth: isSmallMobile ? 280 : 400,
                    paddingHorizontal: isSmallMobile ? utils.spacing.sm : 0,
                  }
                ]}>
                  Create your first portfolio to get started managing your properties
                </Text>
              </BaseCard>
            </View>
          ) : (
            <View style={{
              ...Platform.select({
                web: {
                  display: 'grid',
                  gridTemplateColumns: responsive.isMobile 
                    ? '1fr' 
                    : responsive.isTablet 
                    ? 'repeat(2, 1fr)' 
                    : 'repeat(3, 1fr)',
                  gap: cardGap,
                } as any,
                default: {
                  flexDirection: responsive.isMobile ? 'column' : 'row',
                  flexWrap: 'wrap',
                  gap: cardGap,
                },
              }),
            }}>
              {portfolios.map((portfolio, index) => (
                <View
                  key={portfolio.id}
                  style={{
                    ...Platform.select({
                      web: {
                        width: '100%',
                      } as any,
                      default: {
                        width: responsive.isMobile ? '100%' : `${(100 / gridColumns) - 2}%`,
                        minWidth: responsive.isMobile ? undefined : 280,
                        maxWidth: responsive.isMobile ? '100%' : undefined,
                      },
                    }),
                  }}
                >
                  <BaseCard
                    elevated
                    onPress={() => handlePortfolioPress(portfolio.id)}
                    style={{
                      width: '100%',
                      ...Platform.select({
                        web: {
                          boxShadow: `0 4px 16px ${addColorOpacity('#000', 0.08)}`,
                          transition: 'all 0.2s ease',
                          ':hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 8px 24px ${addColorOpacity('#000', 0.12)}`,
                          } as any,
                          boxSizing: 'border-box' as any,
                        },
                      }),
                    }}
                  >
                      <View style={{
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        marginBottom: isSmallMobile ? utils.spacing.xs : utils.spacing.sm,
                        gap: isSmallMobile ? utils.spacing.xs : utils.spacing.sm,
                      }}>
                        <View style={{
                          width: isSmallMobile ? 32 : getResponsiveValue(36, 40, 44, 48),
                          height: isSmallMobile ? 32 : getResponsiveValue(36, 40, 44, 48),
                          borderRadius: utils.radius.md,
                          backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <Ionicons 
                            name="folder" 
                            size={isSmallMobile ? 16 : getResponsiveValue(18, 20, 22, 24)} 
                            color={tokens.color.primary} 
                          />
                        </View>
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <Text 
                            style={[
                              {
                                fontSize: cardTitleSize,
                                fontWeight: utils.typography.weights.semibold,
                                marginBottom: isSmallMobile ? 2 : utils.spacing.xs,
                                color: tokens.color.onSurface,
                                fontFamily: utils.typography.heading.fontFamily,
                                lineHeight: cardTitleSize * 1.3,
                              }
                            ]}
                            numberOfLines={2}
                            ellipsizeMode="tail"
                          >
                            {portfolio.name}
                          </Text>
                        </View>
                      </View>
                      {portfolio.description && (
                        <Text 
                          style={[
                            {
                              fontSize: cardBodySize,
                              color: addColorOpacity(tokens.color.onSurface, 0.7),
                              marginBottom: isSmallMobile ? utils.spacing.xs : utils.spacing.sm,
                              lineHeight: cardBodySize * 1.5,
                              fontFamily: utils.typography.body.fontFamily,
                            }
                          ]}
                          numberOfLines={isSmallMobile ? 2 : 3}
                          ellipsizeMode="tail"
                        >
                          {portfolio.description}
                        </Text>
                      )}
                      <View style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: isSmallMobile ? 4 : utils.spacing.xs,
                        marginTop: isSmallMobile ? 2 : utils.spacing.xs,
                      }}>
                        <Ionicons 
                          name="calendar-outline" 
                          size={isSmallMobile ? 12 : getResponsiveValue(13, 14, 14, 15)} 
                          color={addColorOpacity(tokens.color.onSurface, 0.5)} 
                        />
                        <Text style={[
                          {
                            fontSize: cardMetaSize,
                            color: addColorOpacity(tokens.color.onSurface, 0.5),
                            fontFamily: utils.typography.body.fontFamily,
                          }
                        ]}>
                          {isSmallMobile ? new Date(portfolio.created_at).toLocaleDateString() : `Created: ${new Date(portfolio.created_at).toLocaleDateString()}`}
                        </Text>
                      </View>
                  </BaseCard>
                </View>
              ))}
            </View>
          )}

      {/* Create Portfolio Modal */}
      <Modal
        visible={createModalVisible}
        transparent={true}
        animationType="none"
        onRequestClose={() => setCreateModalVisible(false)}
      >
        <View
          style={[
            {
              flex: 1,
              backgroundColor: addColorOpacity('#000', 0.5),
              justifyContent: 'center',
              alignItems: 'center',
              padding: utils.spacing.lg,
            },
            Platform.select({
              web: {
                backdropFilter: 'blur(4px)',
              } as any,
            }),
          ]}
        >
          <Pressable
            style={[
              {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }
            ]}
            onPress={() => setCreateModalVisible(false)}
          />
          <View
            style={[
              {
                backgroundColor: tokens.color.surface,
                borderRadius: isSmallMobile ? utils.radius.lg : utils.radius.xl,
                padding: isSmallMobile ? utils.spacing.lg : utils.spacing.xl,
                width: '100%',
                maxWidth: isSmallMobile ? '95%' : responsive.isMobile ? '90%' : 500,
                ...Platform.select({
                  web: {
                    boxShadow: `0 20px 60px ${addColorOpacity('#000', 0.3)}`,
                    boxSizing: 'border-box',
                  } as any,
                  default: {
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 20 },
                    shadowOpacity: 0.3,
                    shadowRadius: 60,
                    elevation: 20,
                  },
                }),
              },
            ]}
          >
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: isSmallMobile ? utils.spacing.md : utils.spacing.lg,
            }}>
              <Text style={[
                {
                  fontSize: isSmallMobile ? 18 : getResponsiveValue(20, 22, 23, 24),
                  fontWeight: utils.typography.weights.bold,
                  color: tokens.color.onSurface,
                  fontFamily: utils.typography.heading.fontFamily,
                  letterSpacing: -0.5,
                }
              ]}>
                Create Portfolio
              </Text>
              <Pressable
                onPress={() => {
                  setCreateModalVisible(false);
                  setPortfolioName('');
                  setPortfolioDescription('');
                }}
                style={({ pressed }) => [
                  {
                    width: isSmallMobile ? 28 : getResponsiveValue(30, 32, 34, 36),
                    height: isSmallMobile ? 28 : getResponsiveValue(30, 32, 34, 36),
                    borderRadius: utils.radius.full,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: addColorOpacity(tokens.color.onSurface, 0.05),
                    ...Platform.select({
                      web: {
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        ':hover': {
                          backgroundColor: addColorOpacity(tokens.color.onSurface, 0.1),
                        },
                      } as any,
                      default: {
                        opacity: pressed ? 0.7 : 1,
                      },
                    }),
                  },
                ]}
              >
                <Ionicons 
                  name="close" 
                  size={isSmallMobile ? 16 : getResponsiveValue(18, 20, 21, 22)} 
                  color={tokens.color.onSurface} 
                />
              </Pressable>
            </View>
            
            <Input
              label="Portfolio name"
              placeholder="Enter portfolio name"
              value={portfolioName}
              onChangeText={(text) => {
                // Limit input length for security
                if (text.length <= 255) {
                  setPortfolioName(text);
                }
              }}
              autoFocus={!isMobile}
              returnKeyType="next"
              maxLength={255}
            />
            
            <Input
              label="Description (optional)"
              placeholder="Enter a brief description"
              value={portfolioDescription}
              onChangeText={(text) => {
                // Limit input length for security
                if (text.length <= 1000) {
                  setPortfolioDescription(text);
                }
              }}
              multiline
              numberOfLines={4}
              maxLength={1000}
              style={{
                minHeight: 100,
                textAlignVertical: 'top',
                marginTop: utils.spacing.md,
              }}
            />

            <View style={{
              flexDirection: 'row',
              gap: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
              justifyContent: 'flex-end',
              marginTop: isSmallMobile ? utils.spacing.lg : utils.spacing.xl,
            }}>
              <View style={{ flex: isSmallMobile ? 1 : 0 }}>
                <Pressable
                  onPress={() => {
                    setCreateModalVisible(false);
                    setPortfolioName('');
                    setPortfolioDescription('');
                  }}
                  style={({ pressed }) => [
                    {
                      backgroundColor: 'transparent',
                      borderWidth: 1,
                      borderColor: tokens.color.border,
                      minHeight: isSmallMobile ? 40 : getResponsiveValue(42, 44, 46, 48),
                      paddingHorizontal: isSmallMobile ? utils.spacing.md : utils.spacing.lg,
                      paddingVertical: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
                      borderRadius: utils.radius.md,
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: isSmallMobile ? undefined : 100,
                      ...Platform.select({
                        web: {
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          ':hover': {
                            borderColor: tokens.color.primary,
                            backgroundColor: addColorOpacity(tokens.color.primary, 0.05),
                          },
                          ':active': {
                            transform: 'scale(0.98)',
                          },
                        } as any,
                        default: {
                          opacity: pressed ? 0.8 : 1,
                        },
                      }),
                    },
                  ]}
                >
                  <Text style={{
                    fontSize: buttonSize,
                    fontWeight: utils.typography.weights.medium,
                    color: tokens.color.onSurface,
                    fontFamily: utils.typography.body.fontFamily,
                  }}>
                    Cancel
                  </Text>
                </Pressable>
              </View>
              <View style={{ flex: isSmallMobile ? 1 : 0 }}>
                <Pressable
                  onPress={handleCreatePortfolio}
                  disabled={creating || !portfolioName.trim()}
                  style={({ pressed }) => [
                    {
                      backgroundColor: tokens.color.primary,
                      minHeight: isSmallMobile ? 40 : getResponsiveValue(42, 44, 46, 48),
                      paddingHorizontal: isSmallMobile ? utils.spacing.md : utils.spacing.lg,
                      paddingVertical: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
                      borderRadius: utils.radius.md,
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: isSmallMobile ? undefined : 100,
                      opacity: creating || !portfolioName.trim() ? 0.6 : 1,
                      ...Platform.select({
                        web: {
                          boxShadow: `0 6px 20px ${addColorOpacity(tokens.color.primary, 0.35)}`,
                          cursor: creating || !portfolioName.trim() ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          ':hover': {
                            transform: creating || !portfolioName.trim() ? 'none' : 'translateY(-2px)',
                            boxShadow: creating || !portfolioName.trim() 
                              ? `0 6px 20px ${addColorOpacity(tokens.color.primary, 0.35)}`
                              : `0 8px 28px ${addColorOpacity(tokens.color.primary, 0.45)}`,
                          },
                          ':active': {
                            transform: 'translateY(0px) scale(0.98)',
                          },
                        } as any,
                        default: {
                          shadowColor: tokens.color.primary,
                          shadowOffset: { width: 0, height: 6 },
                          shadowOpacity: 0.35,
                          shadowRadius: 20,
                          elevation: 8,
                          opacity: pressed || creating || !portfolioName.trim() ? 0.8 : 1,
                        },
                      }),
                    },
                  ]}
                >
                  <Text style={{
                    fontSize: buttonSize,
                    fontWeight: utils.typography.weights.semibold,
                    letterSpacing: 0.2,
                    color: tokens.color.onPrimary,
                    fontFamily: utils.typography.body.fontFamily,
                  }}>
                    {creating ? 'Creating...' : 'Create'}
                  </Text>
                </Pressable>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    </PageContainer>
  );
}
